/**
 * AccessEditorModal — what one person can see in one retailer tree, as ticks:
 *
 *   ☑ Entire Home Hardware              (every sub-retailer and location, incl. later ones)
 *       [x] include sub-retailers
 *     ☐ All of HH East                  (incl. locations added later)
 *         ☐ Saint John West · Saint John
 *     ☐ Fredericton HH · Fredericton
 *
 * Ticking a node ticks and greys everything under it. Save is off until
 * something is ticked; "Remove access" takes them out of the whole tree.
 * Role (Associate / Manager) is per person per tree. Admin-only (English).
 *
 * Writes through adminAccountsApi.setAccountGrants → raSetAccess on the server.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, AlertTriangle, ShieldCheck, MapPin, Building2, CheckSquare, Square, Trash2 } from 'lucide-react';
import { fetchAccountAccess, setAccountGrants, type AccountAccess, type AccessGrant } from '../../services/adminAccountsApi';
import { ConfirmModal } from './ConfirmModal';

export type AccessRole = 'viewer' | 'manager';
export const ACCESS_ROLE_LABEL: Record<AccessRole, string> = { viewer: 'Associate', manager: 'Manager' };
export const ACCESS_ROLE_HELP: Record<AccessRole, string> = {
  viewer: 'Sees estimates in their access, comments and responds, can save an estimate on a customer\'s behalf.',
  manager: 'Everything an Associate does, plus: edits estimate contact details, approves or declines registrations, and sets colleagues\' role and access — within their own access.',
};

interface Props {
  userId: number;
  /** Any retailer in the tree to edit (the root is resolved from it). */
  retailerId: number;
  onClose: () => void;
  /** Fired after a save or removal. */
  onSaved: () => void;
}

export const AccessEditorModal: React.FC<Props> = ({ userId, retailerId, onClose, onSaved }) => {
  const [data, setData] = useState<AccountAccess | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [role, setRole] = useState<AccessRole>('viewer');
  const [nodes, setNodes] = useState<Set<number>>(new Set());      // retailer nodes ticked
  const [locs, setLocs] = useState<Set<number>>(new Set());        // locations ticked
  const [includeChildren, setIncludeChildren] = useState(true);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchAccountAccess(userId).then(d => { if (!cancelled) setData(d); }).catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load'); });
    return () => { cancelled = true; };
  }, [userId]);

  const tree = useMemo(() => {
    if (!data) return null;
    const byId = new Map(data.retailers.map(r => [r.id, r]));
    const any = byId.get(retailerId);
    const rootId = any?.parentRetailerId ?? retailerId;
    const root = byId.get(rootId);
    if (!root) return null;
    const children = data.retailers.filter(r => r.parentRetailerId === rootId).sort((a, b) => a.name.localeCompare(b.name));
    const locsOf = (id: number) => data.locations.filter(l => l.retailerId === id).sort((a, b) => a.name.localeCompare(b.name) || (a.city ?? '').localeCompare(b.city ?? ''));
    return { root, children, locsOf, ids: new Set([rootId, ...children.map(c => c.id)]) };
  }, [data, retailerId]);

  // Seed the ticks from what they hold today.
  useEffect(() => {
    if (!data || !tree) return;
    const n = new Set<number>(); const l = new Set<number>(); let r: AccessRole = 'viewer'; let inc = true; let seen = false;
    for (const m of data.memberships) {
      if (!tree.ids.has(m.retailerId) || m.status === 'rejected') continue;
      if (!seen) { r = m.role; seen = true; }
      if (m.role === 'manager') r = 'manager';
      if (m.allLocations) { n.add(m.retailerId); if (m.retailerId === tree.root.id) inc = !!m.includeChildren; }
      else m.locations.forEach(x => l.add(x.id));
    }
    setNodes(n); setLocs(l); setRole(r); setIncludeChildren(seen ? inc : true); setDirty(false);
  }, [data, tree]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  if (!tree && data) {
    return (
      <ConfirmModal open title="Retailer not found" confirmLabel="Close" onConfirm={onClose} onCancel={onClose}><p className="mb-0">This retailer is no longer in the list.</p></ConfirmModal>
    );
  }

  const rootOn = tree ? nodes.has(tree.root.id) : false;
  const rootCoversAll = rootOn && includeChildren;
  const nodeOn = (id: number) => rootCoversAll || nodes.has(id);
  const nodeLocked = (id: number) => rootCoversAll && id !== tree?.root.id;
  const locOn = (retailer: number, id: number) => nodeOn(retailer) || locs.has(id);
  const locLocked = (retailer: number) => nodeOn(retailer);

  const toggleNode = (id: number) => { setDirty(true); setNodes(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; }); };
  const toggleLoc = (id: number) => { setDirty(true); setLocs(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; }); };

  const grants = (): AccessGrant[] => {
    if (!tree) return [];
    const out: AccessGrant[] = [];
    if (rootOn) out.push({ type: 'retailer', id: tree.root.id, includeChildren });
    if (!rootCoversAll) {
      for (const c of tree.children) if (nodes.has(c.id)) out.push({ type: 'retailer', id: c.id });
      for (const id of locs) {
        const l = data?.locations.find(x => x.id === id);
        if (l && tree.ids.has(l.retailerId) && !nodeOn(l.retailerId)) out.push({ type: 'location', id });
      }
    }
    return out;
  };
  const anyTicked = grants().length > 0;
  const summary = (): string => {
    if (!tree) return '';
    if (rootCoversAll) return `Entire ${tree.root.name}, sub-retailers included`;
    const parts: string[] = [];
    if (rootOn) parts.push(`all of ${tree.root.name}'s own locations`);
    for (const c of tree.children) if (nodes.has(c.id)) parts.push(`all of ${c.name}`);
    const singles = Array.from(locs).filter(id => { const l = data?.locations.find(x => x.id === id); return l && tree.ids.has(l.retailerId) && !nodeOn(l.retailerId); }).length;
    if (singles) parts.push(`${singles} location${singles === 1 ? '' : 's'}`);
    return parts.length ? parts.join(' + ') : 'nothing';
  };

  const save = async (g: AccessGrant[]) => {
    if (!tree) return;
    setBusy(true); setError(null);
    try {
      await setAccountGrants({ userId, retailerId: tree.root.id, role, grants: g });
      onSaved(); onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const name = data ? ([data.account.firstName, data.account.lastName].filter(Boolean).join(' ') || data.account.email) : '';
  const Box: React.FC<{ on: boolean; locked: boolean }> = ({ on, locked }) => on ? <CheckSquare size={16} className={locked ? 'text-secondary' : 'text-primary'} /> : <Square size={16} className="text-muted" />;

  return (
    <>
      <div className="modal d-block" tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="access-editor-title">
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <h5 className="modal-title fw-bold d-flex align-items-center gap-2" id="access-editor-title"><ShieldCheck size={18} /> Access for {name || '…'}{tree ? <> — {tree.root.name}</> : ''}</h5>
                {data && <div className="text-muted small">{data.account.email}</div>}
              </div>
              <button type="button" className="btn-close" aria-label="Close" disabled={busy} onClick={onClose} />
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2 small d-flex align-items-center gap-2"><AlertTriangle size={14} /> {error}</div>}
              {!data || !tree ? (
                <div className="text-center py-4 text-muted"><Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} /></div>
              ) : (
                <>
                  <div className="mb-3">
                    <div className="small fw-semibold mb-1">Role</div>
                    {(['viewer', 'manager'] as AccessRole[]).map(r => (
                      <div key={r} className="form-check">
                        <input id={`ae-role-${r}`} type="radio" className="form-check-input" name="ae-role" checked={role === r} disabled={busy} onChange={() => { setRole(r); setDirty(true); }} />
                        <label htmlFor={`ae-role-${r}`} className="form-check-label"><strong>{ACCESS_ROLE_LABEL[r]}</strong> <span className="text-muted small">— {ACCESS_ROLE_HELP[r]}</span></label>
                      </div>
                    ))}
                  </div>

                  <div className="small fw-semibold mb-1">Access</div>
                  <div className="border rounded p-2 mb-2" style={{ maxHeight: '45vh', overflowY: 'auto' }}>
                    {/* Root */}
                    <div className="d-flex align-items-center gap-2 py-1" role="button" onClick={() => !busy && toggleNode(tree.root.id)}>
                      <Box on={rootOn} locked={false} />
                      <Building2 size={14} className="text-secondary" />
                      <span className="fw-semibold">Entire {tree.root.name}</span>
                      <span className="text-muted small">— every location, including ones added later</span>
                    </div>
                    {rootOn && tree.children.length > 0 && (
                      <div className="form-check form-switch ms-4 mb-1" onClick={e => e.stopPropagation()}>
                        <input id="ae-inc" type="checkbox" className="form-check-input" role="switch" checked={includeChildren} disabled={busy} onChange={e => { setIncludeChildren(e.target.checked); setDirty(true); }} />
                        <label htmlFor="ae-inc" className="form-check-label small">Include its {tree.children.length} sub-retailer{tree.children.length === 1 ? '' : 's'} (and ones added later)</label>
                      </div>
                    )}
                    {/* Root's own locations */}
                    {tree.locsOf(tree.root.id).map(l => (
                      <div key={l.id} className={`d-flex align-items-center gap-2 py-1 ms-4 ${locLocked(tree.root.id) ? 'text-muted' : ''}`} role="button" onClick={() => !busy && !locLocked(tree.root.id) && toggleLoc(l.id)}>
                        <Box on={locOn(tree.root.id, l.id)} locked={locLocked(tree.root.id)} />
                        <MapPin size={13} className="text-secondary" />
                        <span>{l.name}{l.city ? <span className="text-muted"> · {l.city}</span> : null}</span>
                      </div>
                    ))}
                    {/* Sub-retailers */}
                    {tree.children.map(c => (
                      <div key={c.id} className="ms-3 mt-1">
                        <div className={`d-flex align-items-center gap-2 py-1 ${nodeLocked(c.id) ? 'text-muted' : ''}`} role="button" onClick={() => !busy && !nodeLocked(c.id) && toggleNode(c.id)}>
                          <Box on={nodeOn(c.id)} locked={nodeLocked(c.id)} />
                          <span className="text-muted small">↳</span>
                          <Building2 size={14} className="text-secondary" />
                          <span className="fw-semibold">All of {c.name}</span>
                          <span className="text-muted small">— including locations added later</span>
                        </div>
                        {tree.locsOf(c.id).map(l => (
                          <div key={l.id} className={`d-flex align-items-center gap-2 py-1 ms-5 ${locLocked(c.id) ? 'text-muted' : ''}`} role="button" onClick={() => !busy && !locLocked(c.id) && toggleLoc(l.id)}>
                            <Box on={locOn(c.id, l.id)} locked={locLocked(c.id)} />
                            <MapPin size={13} className="text-secondary" />
                            <span>{l.name}{l.city ? <span className="text-muted"> · {l.city}</span> : null}</span>
                          </div>
                        ))}
                        {tree.locsOf(c.id).length === 0 && <div className="text-muted small ms-5">no locations yet</div>}
                      </div>
                    ))}
                    {tree.locsOf(tree.root.id).length === 0 && tree.children.length === 0 && <div className="text-muted small ms-4">no locations yet — only the whole retailer can be granted</div>}
                  </div>
                  <div className={`small ${anyTicked ? 'text-muted' : 'text-danger fw-semibold'}`}>
                    {anyTicked ? <>Will see: {summary()}.</> : 'Nothing ticked — tick the retailer, a sub-retailer or at least one location.'}
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              {data && tree && data.memberships.some(m => tree.ids.has(m.retailerId)) && (
                <button type="button" className="btn btn-outline-danger me-auto d-inline-flex align-items-center gap-1" disabled={busy} onClick={() => setConfirmRemove(true)}><Trash2 size={14} /> Remove from {tree.root.name}</button>
              )}
              <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={onClose}>Cancel</button>
              <button type="button" className="btn btn-primary d-inline-flex align-items-center gap-1" disabled={busy || !anyTicked || !dirty} onClick={() => void save(grants())}>
                {busy && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />} Save access
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop show" />
      <ConfirmModal open={confirmRemove} title={`Remove ${name} from ${tree?.root.name ?? 'this retailer'}?`} confirmLabel="Remove" variant="danger" busy={busy}
        onCancel={() => setConfirmRemove(false)} onConfirm={() => { setConfirmRemove(false); void save([]); }}>
        <p className="mb-0">They lose access to the retailer, its sub-retailers and all its locations. They will be re-matched the next time they sign in.</p>
      </ConfirmModal>
    </>
  );
};

export default AccessEditorModal;
